import { types } from "mobx-state-tree";
import { uniqueId } from "../../utilities/js-utils";
export const kUnknownSharedModel = "unknownSharedModel";
// Generic "super class" of all shared models
export const SharedModel = types.model("SharedModel", {
    // The type field has to be optional because the typescript type created from the sub models
    // is an intersection ('&') of this SharedModel and the sub model.  If this was just:
    //   type: types.string
    // then typescript has errors because the intersection logic means the type field is
    // required when creating a shared model. And we don't want to require the
    // type when creating the shared model. This might be solvable by using the
    // mst snapshot preprocessor to add the type.
    //
    // It could be changed to
    //   type: types.maybe(types.string)
    // Because of the intersection it would still mean the sub models would do the right thing,
    // but if someone looks at this definition of SharedModel, it implies the wrong thing.
    // It might also cause problems when code is working with a generic of SharedModel
    // that code couldn't assume that `model.type` is defined.
    //
    // Since this is optional, it needs a default value, and Unknown seems like the
    // best option for this.
    //
    // Perhaps there is some better way to define this so that there would be an error
    // if a sub type does not override it.
    type: types.optional(types.string, kUnknownSharedModel),
    // if not provided, will be generated
    id: types.optional(types.identifier, () => uniqueId()),
})
    .volatile(self => ({
    indexOfType: -1
}))
    .views(self => ({
    get name() {
        // Overridden by subclasses that support storing a name.
        // If unsupported, should always return undefined.
        return undefined;
    }
}))
    .actions(self => ({
    setIndexOfType(index) {
        self.indexOfType = index;
    },
    setName(name) {
        // Overridden by subclasses that support storing a name.
        throw "This SharedModel type does not implement setName";
    }
}));
export function sharedModelHasExportStableSnapshot(model) {
    return typeof (model === null || model === void 0 ? void 0 : model.exportStableSnapshot) === "function";
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoic2hhcmVkLW1vZGVsLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vc3JjL21vZGVscy9zaGFyZWQvc2hhcmVkLW1vZGVsLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLE9BQU8sRUFBd0IsS0FBSyxFQUFFLE1BQU0saUJBQWlCLENBQUM7QUFDOUQsT0FBTyxFQUFFLFFBQVEsRUFBRSxNQUFNLDBCQUEwQixDQUFDO0FBRXBELE1BQU0sQ0FBQyxNQUFNLG1CQUFtQixHQUFHLG9CQUFvQixDQUFDO0FBRXhELDZDQUE2QztBQUM3QyxNQUFNLENBQUMsTUFBTSxXQUFXLEdBQUcsS0FBSyxDQUFDLEtBQUssQ0FBQyxhQUFhLEVBQUU7SUFDcEQsNEZBQTRGO0lBQzVGLHFGQUFxRjtJQUNyRix1QkFBdUI7SUFDdkIsb0ZBQW9GO0lBQ3BGLDBFQUEwRTtJQUMxRSwyRUFBMkU7SUFDM0UsNkNBQTZDO0lBQzdDLEVBQUU7SUFDRix5QkFBeUI7SUFDekIsb0NBQW9DO0lBQ3BDLDJGQUEyRjtJQUMzRixzRkFBc0Y7SUFDdEYsa0ZBQWtGO0lBQ2xGLDBEQUEwRDtJQUMxRCxFQUFFO0lBQ0YsK0VBQStFO0lBQy9FLHdCQUF3QjtJQUN4QixFQUFFO0lBQ0Ysa0ZBQWtGO0lBQ2xGLHNDQUFzQztJQUN0QyxJQUFJLEVBQUUsS0FBSyxDQUFDLFFBQVEsQ0FBQyxLQUFLLENBQUMsTUFBTSxFQUFFLG1CQUFtQixDQUFDO0lBRXZELHFDQUFxQztJQUNyQyxFQUFFLEVBQUUsS0FBSyxDQUFDLFFBQVEsQ0FBQyxLQUFLLENBQUMsVUFBVSxFQUFFLEdBQUcsRUFBRSxDQUFDLFFBQVEsRUFBRSxDQUFDO0NBQ3ZELENBQUM7S0FDRCxRQUFRLENBQUMsSUFBSSxDQUFDLEVBQUUsQ0FBQyxDQUFDO0lBQ2pCLFdBQVcsRUFBRSxDQUFDLENBQUM7Q0FDaEIsQ0FBQyxDQUFDO0tBQ0YsS0FBSyxDQUFDLElBQUksQ0FBQyxFQUFFLENBQUMsQ0FBQztJQUNkLElBQUksSUFBSTtRQUNOLHdEQUF3RDtRQUN4RCxrREFBa0Q7UUFDbEQsT0FBTyxTQUFTLENBQUM7SUFDbkIsQ0FBQztDQUNGLENBQUMsQ0FBQztLQUNGLE9BQU8sQ0FBQyxJQUFJLENBQUMsRUFBRSxDQUFDLENBQUM7SUFDaEIsY0FBYyxDQUFDLEtBQWE7UUFDMUIsSUFBSSxDQUFDLFdBQVcsR0FBRyxLQUFLLENBQUM7SUFDM0IsQ0FBQztJQUNELE9BQU8sQ0FBQyxJQUFZO1FBQ2xCLHdEQUF3RDtRQUN4RCxNQUFNLGtEQUFrRCxDQUFDO0lBQzNELENBQUM7Q0FDRixDQUFDLENBQUMsQ0FBQztBQWFKLE1BQU0sVUFBVSxrQ0FBa0MsQ0FBQyxLQUFjO0lBQy9ELE9BQU8sT0FBTyxDQUFDLEtBQWEsYUFBYixLQUFLLHVCQUFMLEtBQUssQ0FBVSxvQkFBb0IsQ0FBQSxLQUFLLFVBQVUsQ0FBQztBQUNwRSxDQUFDIn0=